import { z } from 'zod';

export const DeleteTeamEventDetailsSchema = z.object({
	teamIndex: z.number().int().min(0)
});

export type DeleteTeamEventDetails = z.infer<typeof DeleteTeamEventDetailsSchema>;

export const SelectionChangeEventDetailsSchema = z.object({
	rowIndex: z.string(),
	previousValue: z.string(),
	previousLabel: z.string(),
	newValue: z.string(),
	newLabel: z.string(),
	additionalCustomEventIdentifier: z.string().optional()
});

export type SelectionChangeEventDetails = z.infer<typeof SelectionChangeEventDetailsSchema>;

export const SearchInputEventDetailsSchema = z.object({
	value: z.string(),
});

export type SearchInputEventDetails = z.infer<typeof SearchInputEventDetailsSchema>;

export const InputEventDetailsSchema = z.object({
	value: z.string(),
	name: z.string(),
	label: z.string(),
	additionalIdentifier: z.string().optional()
});

export type InputEventDetails = z.infer<typeof InputEventDetailsSchema>;

export const GameSelectionEventDetailsSchema = z.object({
	rowIndex: z.number().int().min(0),
	previousValue: z.string(),
	previousLabel: z.string(),
	newValue: z.string(),
	newLabel: z.string(),
	additionalCustomEventIdentifier: z.string().optional()
});

export type GameSelectionEventDetails = z.infer<typeof GameSelectionEventDetailsSchema>;

export type SelectOption = {
	label: string;
	value: string;
}

export enum RoomName {
	brain = 'brain',
	skills = 'skills',
	action = 'action',
}

export const ScreenContentSchema = z.enum([
	'game points',
	'rules',
	'total points',
	'default',
	'jeopardy-grid',
	'jeopardy-popup',
	'millionaire',
]);

export enum ScreenContentEnum {
	gamePoints = 'game points',
	rules = 'rules',
	totalPoints = 'total points',
	default = 'default',
	jeopardyGrid = 'jeopardy-grid',
	jeopardyPopup = 'jeopardy-popup',
	millionaire = 'millionaire',
}

export type ScreenContent = z.infer<typeof ScreenContentSchema>;

export enum PointCalculationEnum {
	mostPoints = 'most_points',
	fastestTime = 'fastest_time',
	smallestDifference = 'smallest_difference',
}

export const PointCalculationDisplayNames: Record<PointCalculationEnum, string> = {
	[PointCalculationEnum.mostPoints]: 'Meiste Punkte',
	[PointCalculationEnum.fastestTime]: 'Schnellste Zeit',
	[PointCalculationEnum.smallestDifference]: 'Kleinste Abweichung',
};

export const RoomDisplayNames: Record<RoomName, string> = {
	[RoomName.brain]: 'Brain',
	[RoomName.skills]: 'Skills',
	[RoomName.action]: 'Action',
};

export const PointCalculationSchema = z.enum([
	'most_points',
	'fastest_time',
	'smallest_difference',
]);

export type PointCalculation = z.infer<typeof PointCalculationSchema>;

export const CustomerSchema = z.object({
	id: z.string(),
	full_name: z.string().min(1),
	email: z.email(),
	phone: z.string().max(30).optional().nullable(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export type Customer = z.infer<typeof CustomerSchema>;

export const RoundPointSchema = z.object({
	id: z.string(),
	points: z.number().optional().nullable(),
	round: z.number().int(),
	team_fastest_time_seconds: z.number().optional().nullable(),
	team_smallest_difference_value: z.number().optional().nullable(),
	round_target_value: z.number().optional().nullable(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export type RoundPoint = z.infer<typeof RoundPointSchema>;

export enum RoundPointsFieldEnum {
	points = 'points',
	teamFastestTimeSeconds = 'team_fastest_time_seconds',
	teamSmallestDifferenceValue = 'team_smallest_difference_value',
}

export const PlayerSchema = z.object({
	id: z.string(),
	name: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export type Player = z.infer<typeof PlayerSchema>;

export const TeamSchema = z.object({
	id: z.string(),
	name: z.string(),
	color: z.string(),
	team_number: z.number().int(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	players: z.array(PlayerSchema).default([]),
});

export type Team = z.infer<typeof TeamSchema>;

export const GameSchema = z.object({
	id: z.string(),
	name: z.string(),
	point_calculation: PointCalculationSchema,
	default_room: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	description: z.string(),
	rules: z.string(),
	rule_video_url: z.url().or(z.literal(''))
});

export type Game = z.infer<typeof GameSchema>;

export const GamePointSchema = z.object({
	id: z.string(),
	game: z.string().regex(/^Game-[0-9a-fA-F-]{36}$/, 'Ungültige Game ID'),
	team: z.string().regex(/^Team-[0-9a-fA-F-]{36}$/, 'Ungültige Team ID'),
	rounds: z.array(RoundPointSchema),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});

export type GamePoint = z.infer<typeof GamePointSchema>;

export const EventSchema = z.object({
	id: z.string().regex(/^Event-[0-9a-fA-F-]{36}$/, 'Ungültige ID'),
	name: z.string().min(1, 'Das Event benötigt einen Namen'),
	datetime: z.coerce.date({ error: 'Ungültiges Datum' }),
	customer: CustomerSchema,
	teams: z.array(TeamSchema).min(2, 'Es müssen mindestens 2 Teams vorhanden sein'),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	duration: z.number().int('Dauer muss eine ganze Zahl sein'),
	games: z.array(GameSchema),
	game_order: z.record(z.string(), z.string().nullable()),
	game_points: z.array(GamePointSchema),
	notes: z.string(),
});

export type BigBashEvent = z.infer<typeof EventSchema>;

export const GameOrderSnapshotSchema = z.object({
	game_order: z.record(z.string(), z.string().nullable()),
	games: z.array(GameSchema),
});

export type GameOrderSnapshot = z.infer<typeof GameOrderSnapshotSchema>;

export const RoomSchema = z.object({
	id: z.string(),
	display_name: z.string(),
	is_active: z.boolean(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
	current_event: EventSchema.optional().nullable(),
	current_game: GameSchema.optional().nullable(),
	screen_content: ScreenContentSchema.optional().nullable(),
});

export type Room = z.infer<typeof RoomSchema>;


export const WebSocketDataSchema = z.object({
	screenContent: ScreenContentSchema,
	event: EventSchema.optional(),
	game: GameSchema.optional(),
}).extend({
	// Allow optional additional payload fields for custom screen content (jeopardy grid/popup)
	quiz: z.any().optional(),
	question: z.any().optional(),
	showAnswer: z.boolean().optional(),
	selectedOptionId: z.string().optional(),
});

export type WebSocketData = z.infer<typeof WebSocketDataSchema>;

export const schemas = {
	ScreenContentSchema,
	CustomerSchema,
	RoundPointsSchema: RoundPointSchema,
	PlayerSchema,
	TeamSchema,
	GameSchema,
	EventSchema,
	RoomSchema,
	GamePointsSchema: GamePointSchema,
	WebSocketDataSchema,
} as const;

export namespace TS {
	export type ScreenContent = z.infer<typeof ScreenContentSchema>;
	export type Customer = z.infer<typeof CustomerSchema>;
	export type RoundPoints = z.infer<typeof RoundPointSchema>;
	export type Player = z.infer<typeof PlayerSchema>;
	export type Team = z.infer<typeof TeamSchema>;
	export type Game = z.infer<typeof GameSchema>;
	export type Event = z.infer<typeof EventSchema>;
	export type Room = z.infer<typeof RoomSchema>;
	export type GamePoints = z.infer<typeof GamePointSchema>;
	export type WebSocketData = z.infer<typeof WebSocketDataSchema>;
}